import { useMutation } from '@apollo/client';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { Accordion, Button, Card, Spinner } from 'react-bootstrap';
import { PraSection } from '../../../../../../__gqltypes__';
import { RESET_USER_PRA_COOLDOWN } from '../../../graphql';

// The mutation is not in the deployed schema yet, so __gqltypes__ has no
// generated shape for it (same situation as the other untyped dev-tool
// mutations on this screen).
type ResetResult = {
  section: PraSection;
  reset: boolean;
  previousCooldownEndsAt: string | null;
  sessionSnapshotSurvives: boolean;
};

const SECTION_LABEL: Record<PraSection, string> = {
  [PraSection.FREE]: 'Free Rewards',
  [PraSection.CHAINED]: 'Chained Rewards',
};

type Props = {
  userId: string;
};

export function UserPraCooldownDisplay({ userId }: Props): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [results, setResults] = useState<Array<ResetResult> | null>(null);

  const [resetCooldown, { loading }] = useMutation(RESET_USER_PRA_COOLDOWN, {
    onCompleted: (data) => {
      setResults(data?.resetUserProgressiveAdsCooldown?.results ?? []);
    },
  });

  const handleReset = useCallback(
    (sections: Array<PraSection>) => {
      setResults(null);
      resetCooldown({ variables: { input: { userId, sections } } });
    },
    [resetCooldown, userId]
  );

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              Progressive Rewarded Ads cooldown
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <>
              <div className="text-muted mb-2">
                Ends the running cooldown now by pushing the cycle into the past — the feature&apos;s own reset then
                runs on the player&apos;s next read, exactly as it would after a real expiry.
              </div>
              <div className="d-flex align-items-center mt-2">
                {loading ? (
                  <Spinner animation="border" />
                ) : (
                  <>
                    <Button className="mr-2" onClick={() => handleReset([PraSection.FREE])}>
                      <i className="fas fa-hourglass-end mr-2" />
                      Reset Free
                    </Button>
                    <Button className="mr-2" onClick={() => handleReset([PraSection.CHAINED])}>
                      <i className="fas fa-hourglass-end mr-2" />
                      Reset Chained
                    </Button>
                    <Button variant="warning" onClick={() => handleReset([PraSection.FREE, PraSection.CHAINED])}>
                      <i className="fas fa-hourglass-end mr-2" />
                      Reset both
                    </Button>
                  </>
                )}
              </div>
              {results && (
                <div className="mt-3">
                  {results.map((result) => (
                    <div key={result.section}>
                      <div>
                        <b>{SECTION_LABEL[result.section] ?? result.section}</b>:{' '}
                        {result.reset
                          ? `cooldown ended (it was running until ${moment(result.previousCooldownEndsAt).format(
                              'YYYY-MM-DD HH:mm:ss'
                            )})`
                          : 'no cooldown was running — nothing to reset'}
                      </div>
                      {result.sessionSnapshotSurvives && (
                        <div className="text-warning">
                          This player&apos;s current app launch already recorded a reward list, and the reset does not
                          drop it (the next claim does). Restart the app to see an edited configuration.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
}
